import clsx from 'clsx';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import { ConfigContext } from '../WidgetEventsManager';
import { RealTimePrice } from '../hooks/useRealTimePrice';
import style from './ProductsListItemPrice.module.css';

export interface ProductRealTimePriceProps {
  price: RealTimePrice | undefined;
}

const getCurrencyFormatter = (locale: string, currency: string, value: number) => {
  const fractionDigits = value % 1 === 0 ? 0 : 2;
  const formatter = Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return formatter.format(value);
};

export const ProductRealTimePrice = ({ price }: ProductRealTimePriceProps) => {
  const { currencyCode } = useContext(ConfigContext);
  const { i18n, t } = useTranslation();

  const minPriceFormatted = useMemo(
    () => (currencyCode && price ? getCurrencyFormatter(i18n.language, currencyCode, price.min) : undefined),
    [currencyCode, i18n.language, price]
  );

  const originalMinPriceFormatted = useMemo(
    () => (currencyCode && price && price.originalMin !== null ? getCurrencyFormatter(i18n.language, currencyCode, price.originalMin) : undefined),
    [currencyCode, i18n.language, price]
  );

  const priceVaries = useMemo(() => !!price?.varies, [price?.varies]);

  if (!minPriceFormatted || !currencyCode) {
    return <div />;
  }

  return (
    <div className={clsx(style.root)}>
      {priceVaries ? <span className={clsx(globalStyle.textXXSmall, globalStyle.textColorMuted)}>{t('LOC_PRICE_FROM')}</span> : null}
      <div className={clsx(style.prices)}>
        <span className={clsx(globalStyle.textNormal, globalStyle.textBold, { [style.red]: !!originalMinPriceFormatted })}>{minPriceFormatted}</span>
        {originalMinPriceFormatted ? (
          <span className={clsx(globalStyle.textSmall, globalStyle.textBold, globalStyle.textColorMuted, style.sale)}>
            {originalMinPriceFormatted}
          </span>
        ) : null}
      </div>
    </div>
  );
};
